import { PagingMeta } from "@/types/agent"

import { baseApi } from "./base-api"

export type AdminUserStatus =
  | "active"
  | "unverified"
  | "deactivated"
  | "deleted"

/** The field the search matched; matched_value on the row is that field's value. */
export type AdminUserMatchedBy =
  | "id"
  | "chat"
  | "phone"
  | "booking_guest_phone"
  | "flight_billing_phone"
  | "hotel_confirmation"
  | "flight_confirmation"
  | "package_confirmation"
  | "email"
  | "handle"
  | "name"
  | "forwarding_email"
  | "booking_guest_email"
  | "flight_billing_email"

export type AdminUserRow = {
  id: number
  ref: string
  first_name: string | null
  last_name: string | null
  handle: string | null
  email: string | null
  hide_my_email: boolean
  provider: string
  email_verified: boolean
  status: AdminUserStatus
  signed_up_at: string
  last_active_at: string | null
  apps: string[]
  used_ios_app: boolean
  chat_count: number
  booking_count: number
  browser_task_count: number
  failed_browser_task_count: number
  feedback_count: number
  matched_by: AdminUserMatchedBy
  matched_value: string | null
}

export type AdminUserFilters = {
  search: string
  provider?: "apple" | "google" | "email"
  status?: "verified" | "unverified" | "deactivated" | "deleted"
  last_active?: "7d" | "30d" | "90d_plus"
  signed_up_after?: string
  signed_up_before?: string
  used_ios_app?: boolean
  has_bookings?: boolean
  has_browser_tasks?: boolean
  has_failed_browser_task?: boolean
  gave_feedback?: boolean
  include_staff?: boolean
  page?: number
  per_page?: number
}

export type AdminUsersResponse = { data: AdminUserRow[]; meta: PagingMeta }

export type AdminUserEmail = {
  email: string
  source: "account" | "folio_forwarding" | "hotel_booking" | "flight_booking"
  verified_at?: string | null
  booking_id?: number
}

export type AdminUserFeature = {
  name: string
  consumer_enabled_by_default: boolean
  user_override: boolean | null
  effective: boolean | null
}

export type AdminUserAbuse = {
  flag: {
    active: boolean
    reason: string | null
    until: string | null
    user_agent: string | null
    chat_abuse: boolean | null
  } | null
  last_rate_limit: { reason: string; error: string; at: string } | null
  scammer: {
    flagged: boolean
    reason?: string | null
    flagged_at?: string
    flagged_by?: string
    unflagged_at?: string
    unflagged_by?: string
  } | null
  turnstile_failed: boolean
}

export type AdminUserProfile = {
  user: AdminUserRow & {
    time_zone: string | null
    deactivated_at: string | null
    deactivation_reason: string | null
    cleared_at: string | null
    deletion: {
      state: string
      stage: string | null
      created_at: string
      completed_at: string | null
    } | null
    orgids: string[]
  }
  emails: AdminUserEmail[]
  usage: {
    latest_visit: {
      time: string
      platform: string | null
      domain: string | null
    } | null
    apps_last_used_at: Record<string, string>
    device_count: number
    devices_updated_at: string | null
    message_count: number
  }
  chats: {
    id: number
    name: string | null
    vertical: string | null
    organization_id: number | null
    last_message_at: string | null
    message_count: number
  }[]
  bookings: {
    hotel: {
      id: number
      status: string | null
      provider_name: string | null
      check_in: string | null
      check_out: string | null
      created_at: string
      place_name: string | null
      confirmation_codes: string[] | null
      price: number | null
      price_currency: string | null
      guest_email: string | null
      unsubmitted: boolean
    }[]
    flight: {
      id: number
      status: string
      vendor: string
      flight_itinerary_id: number
      created_at: string
      confirmation_code: string | null
      billing_email: string | null
      unsubmitted: boolean
    }[]
    package: {
      id: number
      service: string
      reference: string
      status: string
      created_at: string
    }[]
  }
  browser_tasks: {
    id: number
    instruction: string
    status: string
    pause_kind: string | null
    stop_reason: string | null
    agent_session_id: number
    parent_task_id: number | null
    created_at: string
    updated_at: string
    failed: boolean
  }[]
  booking_authorizations: {
    id: number
    status: string
    booking_type: string
    booking_id: number
    maximum_total: string
    currency: string
    approved_at: string
    expires_at: string
    revoked_at: string | null
  }[]
  support: {
    feedbacks: {
      id: number
      type: string
      category: string | null
      comment: string | null
      status: string | null
      created_at: string
    }[]
    contact_submissions: {
      id: number
      category: string | null
      comment: string | null
      status: string | null
      created_at: string
      email?: string
      first_name?: string
      last_name?: string
      phone?: string
    }[]
  }
  profile: Record<string, unknown> | null
  features: AdminUserFeature[]
  abuse: AdminUserAbuse
}

export interface AdminTestUserParams {
  email: string
  password: string
  skip_assistant_setup: boolean
}

export interface AdminTestUserResponse {
  user: { id: number; email: string; handle: string }
  handoff_code: string
}

export interface AdminTestUserRow {
  id: number
  email: string
  created_at: string
}

export type AdminTestUsersResponse = {
  data: AdminTestUserRow[]
  meta: PagingMeta
}

// The backend treats a missing boolean filter as false, so only set filters are sent.
function toSearchParams(filters: AdminUserFilters): URLSearchParams {
  const searchParams = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== false) {
      searchParams.set(key, String(value))
    }
  })
  return searchParams
}

export const adminUsersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<AdminUsersResponse, AdminUserFilters>({
      query: (filters) => ({
        url: `/api/store-admin/users?${toSearchParams(filters).toString()}`,
      }),
      providesTags: ["AdminUsers"],
    }),
    getAdminUser: builder.query<AdminUserProfile, number>({
      query: (id) => ({ url: `/api/store-admin/users/${id}` }),
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id }],
    }),
    setAdminUserEmailVerification: builder.mutation<
      { id: number; email_validated_at: string | null },
      { id: number; verified: boolean }
    >({
      query: ({ id, verified }) => ({
        url: `/api/store-admin/users/${id}/email_verification`,
        method: "PUT",
        data: { verified },
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    clearAdminUserAccount: builder.mutation<{ id: number }, number>({
      query: (id) => ({
        url: `/api/store-admin/users/${id}/clear_account`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    createAdminTestUser: builder.mutation<
      AdminTestUserResponse,
      AdminTestUserParams
    >({
      query: (data) => ({
        url: "/api/store-admin/test_users",
        method: "POST",
        data,
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    getAdminTestUsers: builder.query<AdminTestUsersResponse, number>({
      query: (page) => ({ url: `/api/store-admin/test_users?page=${page}` }),
      providesTags: ["AdminUsers"],
    }),
    createAdminTestUserHandoffCode: builder.mutation<
      { handoff_code: string },
      number
    >({
      query: (id) => ({
        url: `/api/store-admin/test_users/${id}/handoff_code`,
        method: "POST",
        data: {},
      }),
    }),
    setAdminTestUserPassword: builder.mutation<
      { id: number },
      { id: number; password: string }
    >({
      query: ({ id, password }) => ({
        url: `/api/store-admin/test_users/${id}/password`,
        method: "PUT",
        data: { password },
      }),
    }),

    setAdminUserFeatureFlag: builder.mutation<
      {
        id: number
        feature: string
        user_override: boolean | null
        effective: boolean | null
      },
      { id: number; feature: string; enabled: boolean | null }
    >({
      query: ({ id, feature, enabled }) => ({
        url: `/api/store-admin/users/${id}/feature_flag`,
        method: "PUT",
        data: { feature, enabled },
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            adminUsersApi.util.updateQueryData(
              "getAdminUser",
              id,
              (profile) => {
                profile.features = profile.features.map((feature) =>
                  feature.name === data.feature
                    ? {
                        ...feature,
                        user_override: data.user_override,
                        effective: data.effective,
                      }
                    : feature
                )
              }
            )
          )
        } catch {
          // The caller reports the failed request
        }
      },
    }),
    flagAdminUserAbuse: builder.mutation<
      { id: number },
      { id: number; days: number; reason: string }
    >({
      query: ({ id, days, reason }) => ({
        url: `/api/store-admin/users/${id}/abuse_flag`,
        method: "POST",
        data: { days, reason },
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    clearAdminUserAbuseFlag: builder.mutation<{ id: number }, number>({
      query: (id) => ({
        url: `/api/store-admin/users/${id}/clear_abuse_flag`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: ["AdminUsers"],
    }),
    setAdminUserScammer: builder.mutation<
      { id: number },
      { id: number; flagged: boolean; reason?: string }
    >({
      query: ({ id, flagged, reason }) => ({
        url: `/api/store-admin/users/${id}/scammer`,
        method: "PUT",
        data: { flagged, reason },
      }),
      invalidatesTags: ["AdminUsers"],
    }),
  }),
})

export const {
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useSetAdminUserEmailVerificationMutation,
  useClearAdminUserAccountMutation,
  useCreateAdminTestUserMutation,
  useGetAdminTestUsersQuery,
  useCreateAdminTestUserHandoffCodeMutation,
  useSetAdminTestUserPasswordMutation,
  useSetAdminUserFeatureFlagMutation,
  useFlagAdminUserAbuseMutation,
  useClearAdminUserAbuseFlagMutation,
  useSetAdminUserScammerMutation,
} = adminUsersApi
